// ADDITIONAL DETAIL (owner, 2026-09-25 — D-154): *"The Marketing Category
// field does not justify its own row. Let's treat it like we treat Associated
// Entities."*
//
// ONE ICON, ONE POP-UP (owner, 2026-10-28 — D-425: "I feel like we could
// probably fold the Associated Entities information into the Additional Detail
// pop-up"). The icon at the end of every line, group row and the line-item bulk
// row, and the pop-up it opens, which holds:
//  - a MARKETING line's Marketing Category (its account is a Marketing
//    account), which it owes;
//  - a line copied in from FLEET MAINTENANCE (D-381): that app's own detail —
//    Category, Action, Parts Cost, Labor Cost, Labor Hours and Notes — read-only.
//    Nobody here picks or edits it, and no line owes it, so it is never red. A
//    group row any of whose lines carries it shows each field reading "See
//    specific line items.", since the lines may differ (D-425);
//  - the ASSOCIATED ENTITIES section (`AssociatedEntitiesEditor`), which was
//    its own icon until D-425: editable where the editor writes tags, read-only
//    elsewhere; a group row's lists the tags its lines share, and a bulk row's
//    every tag its lines carry.
// THE ICON SHOWS ON EVERY LINE, WHEREVER IT SITS (owner, 2026-09-28). It lights
// up blue when any part holds something and wears the red border when any part
// is red (`lineDetailIconState` in `../lib/required.ts`).
//
// EVERY VALUE SHOWN RATHER THAN EDITED IS A LOCKED BOX (D-425: "there should be
// no text over gray background but instead input field blocks like we have for
// every other field") — `LockedValue`, the box every locked field uses. Labels
// sit to the left of their boxes in one column. The heading is styled like the
// line table's "Show Group Rows" label.
//
// THE MARKETING CATEGORY IS EDITABLE AT IMPORT CONTROL (D-190; there since
// AP/AR Approval merged into it, D-216), and wherever the Account is (D-192).
// Anywhere else it is a locked box. A hold changes nothing here: a held
// invoice's fields stay editable (owner, 2026-10-03 — D-282).
//
// The field does not save itself. The caller passes its own save wiring —
// the editor's detail debounce and mutex, or the read-only table's one-pick
// write — so this file owns the look and nothing about persistence.
import { Fragment, useEffect, useRef, useState } from 'react'
import { type AdditionalDetailSections } from '@ap/shared'
import { Caption1, makeStyles, tokens } from '@fluentui/react-components'
import { ClipboardTaskListLtrFilled, ClipboardTaskListLtrRegular } from '@fluentui/react-icons'
import { searchMarketingCategories } from '../lib/data'
import { transportationDetailRows, transportationRollUpRows } from '../lib/format'
import { type AdditionalDetailState } from '../lib/required'
import type { Invoice, InvoiceLineItem, LineItemTransportationDetail } from '../lib/types'
import { useLineItemPanelStyles, useRequiredStyles } from '../lib/theme'
import { AssociatedEntitiesEditor } from './AssociatedEntitiesEditor'
import { EntityPicker, type EntityOption } from './EntityPicker'
import { LockedValue } from './LockedValue'
import { LineItemPopover } from './LineItemIconButton'

/** The name on the icon and the heading in the pop-up. */
export const ADDITIONAL_DETAIL_LABEL = 'Additional Detail'

/** What the pop-up says when it has nothing to show (owner, 2026-10-23 — D-407):
    no Marketing Category or Fleet Maintenance detail, and no Associated Entities
    section, which shows wherever there is an invoice to tag. */
export const NOTHING_OWED_NOTE = 'No additional detail is required for this line item.'

/** What an unsaved line's Associated Entities say: a tag needs a stored line. */
const SAVE_TO_TAG_NOTE = 'Save the invoice to tag entities on this line.'

const useStyles = makeStyles({
  // Label, then box, one pair per row, labels in one column.
  grid: {
    display: 'grid',
    gridTemplateColumns: '170px minmax(0, 1fr)',
    alignItems: 'center',
    columnGap: '10px',
    rowGap: '6px',
  },
  label: { fontSize: '12px', color: tokens.colorNeutralForeground2, whiteSpace: 'nowrap' },
  section: { display: 'flex', flexDirection: 'column', gap: '8px' },
})

/** The Marketing Category section. */
interface CategorySection {
  /** The picked category's id, and its "code — context" text (`detailCategoryLabel`). */
  categoryId: string | null
  label: string
  /** Present = editable here. Absent = a locked box. */
  onSelect?: (option: EntityOption) => void
  /** A write in flight for this line. */
  saving?: boolean
  /** Remount key, so a refused pick re-seeds from the stored value. */
  pickerKey?: string
  /** The refusal message's id while this category's write is refused (C1-09). */
  describedBy?: string
}

/** The Associated Entities section (D-425; its own icon until then). */
export interface EntitiesSection {
  invoice: Invoice
  lineItem: InvoiceLineItem
  /** The viewer may add, edit and remove tags here: the editor's steps (D-216, D-328). */
  editable: boolean
  /** A line not saved yet has no id to hang a tag on. */
  unsaved?: boolean
  /** Fired after a tag is added, edited or removed. */
  onChanged?: () => void
  /** List these lines' tags instead of `lineItem`'s, read-only: a group row's
      (`shared`, the tags its lines agree on — D-425) or the bulk row's
      (`union`, every tag its lines carry — D-242). */
  lineItemIds?: readonly string[]
  childMode?: 'union' | 'shared'
}

interface Props {
  sections: AdditionalDetailSections
  state: AdditionalDetailState
  marketing?: CategorySection
  /** Fleet Maintenance's detail for the line, shown read-only; `null` when it has none. */
  transportation?: LineItemTransportationDetail | null
  /** A group row any of whose lines carries Fleet Maintenance's detail: each
      field reads "See specific line items." (D-425). */
  transportationRollUp?: boolean
  /** A sentence in place of the Marketing and Fleet fields — an unsaved line has
      nothing to write to yet. */
  hint?: string
  /** The Associated Entities section; absent where there is no invoice. */
  entities?: EntitiesSection
  mountNode?: HTMLElement | null
  /** The icon shows but does not open — the line-item bulk row's modes that
      write no Additional Detail (D-242). */
  disabled?: boolean
}

export function AdditionalDetailPopover({ mountNode, disabled, ...panel }: Props) {
  return (
    <LineItemPopover
      regularIcon={ClipboardTaskListLtrRegular}
      filledIcon={ClipboardTaskListLtrFilled}
      label={ADDITIONAL_DETAIL_LABEL}
      filled={panel.state.filled}
      requiredMissing={panel.state.requiredMissing}
      flagged={!!panel.state.flagged}
      mountNode={mountNode}
      disabled={disabled}
    >
      <AdditionalDetailPanel {...panel} />
    </LineItemPopover>
  )
}

function AdditionalDetailPanel({
  sections,
  state,
  marketing,
  transportation,
  transportationRollUp = false,
  hint,
  entities,
}: Omit<Props, 'mountNode' | 'disabled'>) {
  const ps = useLineItemPanelStyles()
  const rs = useRequiredStyles()
  const s = useStyles()
  // Listboxes mount INSIDE the pop-up, so picking an option is not an outside
  // click that dismisses it — the Associated Entities editor's own fix.
  const rootRef = useRef<HTMLDivElement>(null)
  const [popupHost, setPopupHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setPopupHost(rootRef.current)
  }, [])

  const detailRows = transportationRollUp
    ? transportationRollUpRows()
    : transportation
      ? transportationDetailRows(transportation)
      : []
  const owed = (sections.marketing && !!marketing) || detailRows.length > 0
  const live = !hint

  return (
    <div className={ps.root} ref={rootRef}>
      <div className={ps.head}>
        <span className={ps.headLabel}>{ADDITIONAL_DETAIL_LABEL}</span>
      </div>
      {/* Under the heading whenever the line owes nothing, Associated Entities
          or not (owner, 2026-10-28). */}
      {!owed && <Caption1 className={ps.note}>{NOTHING_OWED_NOTE}</Caption1>}
      {owed && hint && <Caption1 className={ps.note}>{hint}</Caption1>}
      {owed && !hint && (
        <div className={s.grid}>
          {sections.marketing && marketing && (
            <>
              <span className={s.label}>Marketing Category</span>
              {live && marketing.onSelect !== undefined ? (
                <EntityPicker
                  key={marketing.pickerKey}
                  size="small"
                  ariaLabel="Marketing Category"
                  mountNode={popupHost}
                  invalid={state.marketingInvalid}
                  // Busy, not disabled, while it saves: focus stays put (C2-06).
                  busy={marketing.saving}
                  describedBy={marketing.describedBy}
                  value={{ id: marketing.categoryId ?? undefined, name: marketing.categoryId ? marketing.label : undefined }}
                  search={(t) =>
                    searchMarketingCategories(t).then<EntityOption[]>((cs) =>
                      cs.map((c) => ({ id: c.id, primary: c.category, secondary: c.context })),
                    )
                  }
                  onSelect={(o) => marketing.onSelect?.(o)}
                />
              ) : (
                // Locked here: the box every locked field is (D-425), red on a
                // group whose lines disagree (D-406, D-416).
                <LockedValue
                  boxed
                  value={marketing.categoryId ? marketing.label : ''}
                  ariaLabel="Marketing Category"
                  className={state.marketingInvalid ? rs.requiredEmpty : undefined}
                />
              )}
            </>
          )}
          {/* Fleet Maintenance's detail: locked boxes, never a control (D-381, D-425). */}
          {detailRows.map((r) => (
            <Fragment key={r.label}>
              <span className={s.label}>{r.label}</span>
              <LockedValue boxed value={r.value} ariaLabel={r.label} />
            </Fragment>
          ))}
        </div>
      )}
      {entities &&
        (entities.unsaved ? (
          <div className={s.section}>
            <div className={ps.head}>
              <span className={ps.headLabel}>Associated Entities</span>
            </div>
            <Caption1 className={ps.note}>{SAVE_TO_TAG_NOTE}</Caption1>
          </div>
        ) : (
          <AssociatedEntitiesEditor
            invoice={entities.invoice}
            lineItem={entities.lineItem}
            readOnly={!entities.editable || entities.lineItemIds !== undefined}
            onChanged={entities.onChanged}
            childIds={entities.lineItemIds}
            childMode={entities.childMode}
          />
        ))}
    </div>
  )
}
